import {QueryClient, useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from "react-query";
import {DatasetsApi} from "../DatasetsApi";
import {ScheduledTasksApi} from "../ScheduledTasksApi";
import {ProductTemplatesApi} from "../ProductTemplatesApi";
import {APIPromise, APIResponse} from "../../services/HttpService";
import {UseQueryOptions} from "react-query/types/react/types";
import {ITableDataEntry} from "../../components/dashboard/product-template-table/utils/interfaces";
import {UserDataApi} from "../UserDataApi";
import {DatasetDataInfo} from "../DatasetDataApi";
import {DatasetDataLagLeadRequest} from "../requests";
import {DashboardApi} from "../DashboardApi";
import {Dashboard} from "../model/dashboard/Dashboard";
import {DashboardReportsApi} from "../DashboardReportsApi";
import {ReportRequestType} from "../model/requests/ReportRequestModels";
import {GeneratedDashboardReportResponse} from "../apiModels";

interface IQueryOptions<T = any> {
    enabled?: boolean;
    noCache?: boolean;
    cacheTime?: number
    staleTime?: number;
    refetchInterval?: number | boolean;
    onSuccess?: (data?: T) => void;
}


interface IMutationOptions {
    onSuccess?: (data?: any) => void;
    onFailure?: (data?: any) => void;
    onMutate?: (data?: any) => void;
}

enum QueryKey {
    DATASET_CATEGORIES = "dataset-categories",
    DATASET_DATA_INFO = "dataset-data-info",
    DATASET_INFOS = "dataset-infos",
    USER_INFO = "user-info",
    BASE_DATASETS = "base-datasets",
    AIRFLOW_DAG_TASK_INFOS = "airflow-dag-task-infos",
    PRODUCT_TEMPLATE_INFOS = "product-template-infos",
    PAGINATED_DATASETS = "paginated-datasets-list",
    DATASETS_COUNT = "count",
    PRODUCT_TEMPLATE_CONFIGS = "product-template-configs",
    PRODUCT_TEMPLATE_DATA = "product-template-data",
    SCHEDULED_TASK_HISTORIES = "scheduled-task-histories",
    PRODUCT_TEMPLATE_FOREIGN_KEYS = "product-template-foreign-keys",
    DASHBOARD = "dashboard",
    GENERATED_REPORT_DATA = "generated-report-data"
}

function defaultResponseHandler<T>(res: APIResponse<T>): T | undefined {
    if (!res.error) {
        return res.data;
    } else {
        console.log(res.error.message)
        throw res.error;
    }
}

const defaultQueryOptions: IQueryOptions = {
    enabled: true,
    noCache: false,
    refetchInterval: false,
} as IQueryOptions

const defaultMutationOptions: IMutationOptions = {
    onSuccess: () => {
    },
    onFailure: () => {
    },
    onMutate: () => {
    },
} as IMutationOptions

function useQueryWrapper<T>(
    queryKey: QueryKey | Array<QueryKey | string | null | undefined>,
    apiPromiseFn: () => APIPromise<T>,
    promiseHandler?: ((res: APIResponse<T>) => T) | null,
    options?: IQueryOptions
): UseQueryResult<T | undefined, Error> {
    const usedOptions = {...defaultQueryOptions, ...options};
    const usedPromiseHandler = promiseHandler ?? defaultResponseHandler;

    const queryOptions = {
        enabled: usedOptions.enabled,
        cacheTime: usedOptions.cacheTime ?? 1000 * 60 * 5,
        staleTime: usedOptions.staleTime ?? 1000 * 60,
        refetchInterval: usedOptions.refetchInterval,
        onSuccess: usedOptions.onSuccess
    } as UseQueryOptions

    if (usedOptions.noCache) {
        queryOptions.cacheTime = 0;
        queryOptions.staleTime = 0;
    }

    return useQuery(queryKey, () => apiPromiseFn().then(usedPromiseHandler), queryOptions as any);
}

export function useUpdateProductTemplateEntry(params: { safeName: string, dateFrom?: string, dateTo?: string }, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, { entryId: string, body: any }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();
    return useMutation(((data: { entryId: string, body: any }) => ProductTemplatesApi.updateProductTemplateEntry({safeName: params.safeName, ...data}).then(defaultResponseHandler)), {
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        },
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo], (oldQueryData: any) => {
                return oldQueryData.map((obj: any) => {
                    if (obj.id === data.id) {
                        return data
                    } else {
                        return obj;
                    }
                });
            });
            options.onSuccess!!();
        }
    })
}

export function useDeleteProductTemplateEntry(params: { safeName: string, dateFrom?: string, dateTo?: string }, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, { entryId: string }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();
    return useMutation(((data: { entryId: string }) => ProductTemplatesApi.deleteProductTemplateEntry(params.safeName, data.entryId).then(defaultResponseHandler)), {
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo], (oldQueryData: any) => {
                return oldQueryData.filter((e: ITableDataEntry) => e.id !== data.id)
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

export function useCreateProductTemplateEntry(params: { safeName: string, dateFrom?: string, dateTo?: string }, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, { body: any }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();
    return useMutation(((data: { body: any, }) => ProductTemplatesApi.createProductTemplateEntry(params.safeName, data.body).then(defaultResponseHandler)), {
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo], (oldQueryData: any) => {
                return [
                    data,
                    ...oldQueryData,
                ]
            });

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

export function useSignProductTemplateEntry(params: { safeName: string, dateFrom?: string, dateTo?: string }, specOptions?: IMutationOptions): UseMutationResult<APIResponse<any>, unknown, { entryId: string, referenceName: string }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();
    let entryId = "";
    let referenceName = "";
    return useMutation((signData: { entryId: string, referenceName: string }) => (ProductTemplatesApi.signProductTemplateEntry(params.safeName, signData.entryId, signData.referenceName).then(defaultResponseHandler))
            .finally(() => {
                entryId = signData.entryId;
                referenceName = signData.referenceName;
            }),
        {
            onSuccess: (newData) => {
                queryClient.setQueryData([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo], (oldQueryData: any) => {
                    return oldQueryData.map((obj: any) => {
                        if (obj.id === entryId) {
                            if (obj.entries[referenceName] != null) {
                                obj.entries[referenceName].value = newData;
                            }
                        }
                        return obj
                    })
                });
                options.onSuccess!!();
            },
            onError: error => {
                options.onFailure!!(error)
            },
            onMutate: variables => {
                options.onMutate!!(variables)
            }
        })
}

export const useGetDatasetCategories = () => useQueryWrapper(QueryKey.DATASET_CATEGORIES, () => DatasetsApi.getDatasetCategories());

export const useGetAirflowDagTaskInfos = (dagId: string) => useQueryWrapper([QueryKey.AIRFLOW_DAG_TASK_INFOS, dagId], () => ScheduledTasksApi.getAirflowTaskDataInfos(dagId));
export const useGetProductTemplateInfos = () => useQueryWrapper(QueryKey.PRODUCT_TEMPLATE_INFOS, () => ProductTemplatesApi.getProductTemplateInfos());
export const useGetProductTemplateInfo = (id: number | null, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_INFOS, id?.toString()], () => ProductTemplatesApi.getProductTemplateInfo(id), null, options);

export const useGetProductTemplateConfig = (refName: string, params?: { usePromiseHandler?: (((res: APIResponse<any>) => any) | null | undefined) }) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_CONFIGS, refName], () => ProductTemplatesApi.getProductTemplateConfig(refName), params?.usePromiseHandler);

export const useGetProductTemplateData = (params: { safeName: string, dateFrom?: string, dateTo?: string }, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_DATA, params.safeName, params.dateFrom, params.dateTo], () => ProductTemplatesApi.getProductTemplateData(params), null, options);

export const useGetProductTemplateForeignKeys = (templateName: string, options?: IQueryOptions) => useQueryWrapper([QueryKey.PRODUCT_TEMPLATE_FOREIGN_KEYS, templateName], () => ProductTemplatesApi.getProductTemplateForeignKeys(templateName), null, options);

export const useGetDatasetInfo = (identifier: string | null, options?: IQueryOptions) => useQueryWrapper([QueryKey.DATASET_INFOS, identifier], () => DatasetsApi.getDatasetInfo(identifier), null, options);

export const useSearchDatasets = (query: string, limit: number, options?: IQueryOptions) => useQueryWrapper([QueryKey.DATASET_INFOS, query, limit.toString()], () => DatasetsApi.queryDatasets(query, limit), null, options);

export const useGetBaseDataset = (identifier: string | null, options?: IQueryOptions) => useQueryWrapper([QueryKey.BASE_DATASETS, identifier], () => DatasetsApi.getBaseDataset(identifier), null, options);

export const useGetAirflowTaskHistories = (dagId: string, taskId: string) => useQueryWrapper([QueryKey.SCHEDULED_TASK_HISTORIES], () => ScheduledTasksApi.getAirflowTaskHistories(dagId, taskId));

export const useGetPaginatedDatasets = (query?: string | null, page?: number | null, size?: number | null, options?: IQueryOptions) =>
    useQueryWrapper([QueryKey.PAGINATED_DATASETS, query, `${page}[${size}]`], () => DatasetsApi.getPaginatedDatasetsList(query, page, size), null, options);

export const useGetDatasetsCount = (options?: IQueryOptions) => useQueryWrapper([QueryKey.DATASETS_COUNT], () => DatasetsApi.getDatasetsCount(), null, options);

export const useGetUserInfo = (username: string, options?: IQueryOptions) => useQueryWrapper([QueryKey.USER_INFO, username], () => UserDataApi.getDataInfo(username), null, options);

export const useGetDatasetDataInfo = (identifiers: Array<string>, config?: DatasetDataLagLeadRequest, options?: IQueryOptions) => useQueryWrapper([QueryKey.DATASET_DATA_INFO, ...identifiers], () => DatasetDataInfo.getDataInfo(identifiers, config), null, options);

export const useGetDataInfoRange = (identifiers: Array<string>, params: { uniqueKey?: string, timeStart: string, timeEnd?: string, usePromiseHandler?: (((res: APIResponse<any>) => any) | null | undefined) }, options?: IQueryOptions) => useQueryWrapper([QueryKey.DATASET_DATA_INFO, params.uniqueKey, ...identifiers, params.timeStart, params.timeEnd || params.timeStart], () => DatasetDataInfo.getDataInfoRange(identifiers, params.timeStart, params.timeEnd), params.usePromiseHandler, options);

export const useGetRecentData = (identifiers: Array<string>, options: {count?: number, options?: IQueryOptions, usePromiseHandler?: (((res: APIResponse<Array<any>>) => any) | null | undefined)}) => useQueryWrapper([QueryKey.DATASET_DATA_INFO, ...identifiers, options.count?.toString()], () => DatasetDataInfo.getRecentData(identifiers, options.count), options.usePromiseHandler, options.options);

export const useGetDashboard = (dashboardId: number, options?: IQueryOptions<Dashboard>) => useQueryWrapper([QueryKey.DASHBOARD, dashboardId.toString()], () => DashboardApi.getDashboard(dashboardId), null, options);

export const useGetDashboardsInfo = (dashboardsIds: Array<number>, options?: IQueryOptions) => useQueryWrapper([QueryKey.DASHBOARD, ...dashboardsIds.toString()], () => DashboardApi.getDashboardsInfos(dashboardsIds), null, options);


export const useUpdateDashboard = (specOptions?: IMutationOptions) => {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();
    return useMutation((dashboard: Dashboard) => DashboardApi.updateDashboard(dashboard).then(defaultResponseHandler), {
        onSuccess: (data) => {
            queryClient.setQueryData([QueryKey.DASHBOARD, data!!.id.toString()], () => {
                return data
            });
            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

export const useGetGeneratedReportData = (params: ReportRequestType, options?: IQueryOptions) => useQueryWrapper([QueryKey.GENERATED_REPORT_DATA, ...params.id.map(e => e.toString()), params.onlyType?.toString(), params.from, params.to], () => DashboardReportsApi.getGeneratedReports(params), null, options);

export function useDeleteReportGeneration(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse | undefined, unknown, { genId: number }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { genId: number }) => DashboardReportsApi.deleteGeneration(data.genId).then(defaultResponseHandler)), {
        onSuccess: (data) => {
            const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA)
            queryKeys.forEach((key) => {
                queryClient.setQueryData(key, (oldQueryData: any) => {
                    return oldQueryData?.filter((e: GeneratedDashboardReportResponse) => e.id !== data?.id)
                });
            })

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

export function useDeleteSingleReport(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse | undefined, unknown, { genId: number, reportId: string }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { genId: number, reportId: string }) => DashboardReportsApi.deleteReport(data.genId, data.reportId).then(defaultResponseHandler)), {
        onSuccess: (data) => {
            const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA)
            queryKeys.forEach((key) => {
                queryClient.setQueryData(key, (oldQueryData: any) => {
                    return oldQueryData?.map((e: GeneratedDashboardReportResponse) => {
                        if (data && e.id === data.id) {
                            if (data.fileIds.length > 0) {
                                return data
                            } else {
                                return undefined
                            }
                        }
                        return e
                    }).filter(function (item: any) {
                        return typeof item !== "undefined" && item !== null;
                    });
                });
            })

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

export function useMarkForRegeneration(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse[] | undefined, unknown, { genId: Array<number>, lazy?: boolean }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { genId: Array<number>, lazy?: boolean }) => DashboardReportsApi.markForRegeneration({
            genId: [...data.genId],
            lazyRegen: data.lazy || false
        })
            .then(defaultResponseHandler)),
        {
            onSuccess: (data) => {
                const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA)
                queryKeys.forEach((key) => {
                    const updatedData = data ? new Map(data.map((item: GeneratedDashboardReportResponse) => [item.id, item])) : null;

                    queryClient.setQueryData(key, (oldQueryData: GeneratedDashboardReportResponse[] | undefined) => {
                        if (!oldQueryData) {
                            return [];
                        }

                        return oldQueryData.map((item: GeneratedDashboardReportResponse) => {
                            const updatedItem = updatedData?.get(item.id);
                            return updatedItem ? updatedItem : item;
                        });
                    });

                })

                options.onSuccess!!();
            },
            onError: error => {
                options.onFailure!!(error)
            }
        })
}

export function useMarkForRegenerationRange(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse[] | undefined, unknown, { dashboardId: number, from: string, to: string, fillMissingSlots?: boolean }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { dashboardId: number, from: string, to: string, fillMissingSlots?: boolean }) => DashboardReportsApi.markForRegenerationRange({...data})
            .then(defaultResponseHandler)),
        {
            onSuccess: () => {
                const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA);
                queryKeys.forEach((key) => {
                    queryClient.refetchQueries({queryKey: key})
                })

                options.onSuccess!!();
            },
            onError: error => {
                options.onFailure!!(error)
            },
            onMutate: variables => {
                options.onMutate!!(variables)
            }
        })
}

export function useGenerateReport(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse | undefined, unknown, { dashboardId: number, referenceTime: string }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { dashboardId: number, referenceTime: string }) => DashboardReportsApi.generateReport({
            dashboardId: data.dashboardId,
            referenceTime: data.referenceTime
        })
            .then(defaultResponseHandler)),
        {
            onSuccess: (data) => {
                const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA)
                queryKeys.forEach((key) => {
                    queryClient.setQueryData(key, (oldQueryData: any) => {
                        if (!data && (!oldQueryData || oldQueryData.length === 0)) {
                            return []
                        }

                        if (!data && oldQueryData) {
                            return oldQueryData
                        }

                        if (data && (!oldQueryData || oldQueryData.length === 0)) {
                            return [data]
                        }

                        let i = oldQueryData.length - 1;

                        while (i >= 0 && (new Date(data.referenceTime) > new Date(oldQueryData[i].referenceTime) ||
                            (new Date(data.referenceTime).getTime() === new Date(oldQueryData[i].referenceTime).getTime() &&
                                new Date(data.generationTimeStart) > new Date(oldQueryData[i].generationTimeStart)))) {
                            i--;
                        }

                        oldQueryData.splice(i + 1, 0, data);

                        return oldQueryData;
                    });
                })
                options.onSuccess!!();
            },
            onError: error => {
                options.onFailure!!(error)
            },
            onMutate: variables => {
                options.onMutate!!(variables)
            }
        })
}

export function useDeleteReportRange(specOptions?: IMutationOptions): UseMutationResult<GeneratedDashboardReportResponse[] | undefined, unknown, { dashboardId: number, from: string, to: string }> {
    const options = {...defaultMutationOptions, ...specOptions}
    const queryClient = useQueryClient();

    return useMutation(((data: { dashboardId: number, from: string, to: string }) => DashboardReportsApi.deleteReportsRange(data.dashboardId, {
        from: data.from,
        to: data.to
    }).then(defaultResponseHandler)), {
        onSuccess: (data) => {
            const queryKeys = getRelatedCacheKeys(queryClient, QueryKey.GENERATED_REPORT_DATA)
            queryKeys.forEach((key) => {
                queryClient.setQueryData(key, (oldQueryData: any) => {
                    return oldQueryData?.map((e: GeneratedDashboardReportResponse) => {
                        if (data && data.some(ob => ob.id === e.id)) {
                            return undefined
                        }
                        return e
                    }).filter(function (item: any) {
                        return typeof item !== "undefined" && item !== null;
                    });
                });
            })

            options.onSuccess!!();
        },
        onError: error => {
            options.onFailure!!(error)
        },
        onMutate: variables => {
            options.onMutate!!(variables)
        }
    })
}

const getRelatedCacheKeys = (queryClient: QueryClient, targetKey: string) => {
    return queryClient.getQueryCache().getAll()
        .map(query => query.queryKey)
        .filter(key => Array.isArray(key) ? key.includes(targetKey) : key === targetKey)
}